import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import TicketsSidebar, {
  TICKETS_NAV_SIDEBAR_WIDTH,
} from "../../../components/dashboardcomponents/TicketV2/TicketsSidebar";
import TicketList from "../../../components/dashboardcomponents/TicketV2/TicketList";
import TicketChat from "../../../components/dashboardcomponents/TicketV2/TicketChat";
import TicketDetails from "../../../components/dashboardcomponents/TicketV2/TicketDetails";
import TicketDetailsEmpty from "../../../components/dashboardcomponents/TicketV2/TicketDetailsEmpty";
import CreateTicket from "../../../components/dashboardcomponents/TicketSectionComponents/CreateTicket/CreateTicket";
import {
  getAllTickets,
  addTicketOptimistic,
  updateTicketOptimistic,
  removeTicketOptimistic,
  adjustStatusCountsForNewTicket,
  adjustStatusCountsForStatusChange,
  buildTicketListParams,
} from "../../../store/features/getAllTickets";
import { getTicketById } from "../../../store/features/getTicketById";
import {
  getTicketMessagesById,
  resetMessagesState,
} from "../../../store/features/getTicketMessagesById";
import { useSocket } from "../../../SocketContext";
import { buildTv2Ticket } from "../../../utils/ticketV2Mappers";
import { TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import useDebounce from "../../../hooks/useDebounce";

const statusMap = {
  1: "open",
  2: "pending",
  3: "solved",
  4: "spam",
};

const reverseStatusMap = {
  open: 1,
  pending: 2,
  solved: 3,
  spam: 4,
};

function tabIndexFromSearchParams(searchParams) {
  const tabParam = searchParams.get("tab");
  if (tabParam && reverseStatusMap[tabParam] !== undefined) {
    return reverseStatusMap[tabParam];
  }
  return 0;
}

/** Ticket V2 list defaults to open/pending status filter (unlike legacy "all"). */
function buildTv2TicketListParams(searchParams, limit = 10, search) {
  const tabIdx = tabIndexFromSearchParams(searchParams);
  const params = buildTicketListParams(tabIdx, searchParams, limit);
  const view = searchParams.get("view") || "";
  const tab = searchParams.get("tab") || "";
  if (
    !params.status &&
    tab !== "solved" &&
    tab !== "spam" &&
    view !== "spam"
  ) {
    params.status = "open";
  }
  if (typeof search === "string") {
    const term = search.trim();
    if (term) params.search = term;
    else delete params.search;
  }
  if (!params.order) params.order = "desc";
  return params;
}

function shouldDefaultListStatus(searchParams) {
  const view = searchParams.get("view") || "";
  const tab = searchParams.get("tab") || "";
  const status = searchParams.get("status") || "";
  if (tab === "solved" || tab === "spam" || view === "spam") return false;
  return status !== "open" && status !== "pending" && status !== "solved";
}

function TicketV2() {
  const dispatch = useDispatch();
  const socket = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const [ticketsNavOpen, setTicketsNavOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [ticketSearch, setTicketSearch] = useState("");
  const debouncedTicketSearch = useDebounce(ticketSearch, 350);
  const debouncedTicketSearchRef = useRef(debouncedTicketSearch);
  debouncedTicketSearchRef.current = debouncedTicketSearch;
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const processedMessageIdsRef = useRef(new Set());

  const listFetchKey = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("ticketId");
    p.delete("group_id");
    // Treat missing status as open so the first-load URL sync does not refetch.
    if (shouldDefaultListStatus(searchParams)) {
      p.set("status", "open");
    }
    const term = debouncedTicketSearch.trim();
    if (term) p.set("search", term);
    else p.delete("search");
    return p.toString();
  }, [searchParams, debouncedTicketSearch]);

  const {
    tickets: allTickets,
    loading: allTicketsLoading,
    statusCounts,
    listLoadingMore,
    listExhausted,
    initialLoaded: ticketsInitialLoaded,
    pagination: ticketsPagination,
    statusCountRefreshRequests = 0,
  } = useSelector((state) => state.tickets);

  const { data: ticketDetailData, loading: ticketDetailLoading } = useSelector(
    (state) => state.getTicketById
  );
  const {
    messages: ticketMessages,
    loading: messagesLoading,
  } = useSelector((state) => state.ticketMessages);

  const userData = useSelector((state) => state.loginToken);
  const currentUserId = userData?.response?.user?._id;

  const ticketIdParam = searchParams.get("ticketId");
  const viewParam = searchParams.get("view") || "";

  const listStatusFilterOptions = useMemo(() => {
    const base = [
      { id: "open", label: "Open" },
      { id: "pending", label: "Pending" },
    ];
    if (viewParam === "assigned_to_me" || viewParam === "assigned") {
      return [...base, { id: "solved", label: "Solved" }];
    }
    return base;
  }, [viewParam]);

  const ticketCounts = useMemo(() => {
    const sc = statusCounts || {};
    const sumByStatus =
      (sc.open || 0) +
      (sc.pending || 0) +
      (sc.assigned_to_me || 0) +
      (sc.unassigned || 0) +
      (sc.closed || 0) +
      (sc.spam || 0);
    return {
      all: sc.all > 0 ? sc.all : sumByStatus,
      open: sc.open || 0,
      pending: sc.pending || 0,
      assigned_to_me: sc.assigned_to_me || 0,
      unassigned: sc.unassigned || 0,
      closed: sc.closed || sc.solved || 0,
      spam: sc.spam || 0,
    };
  }, [statusCounts]);

  const listStatusFilter = useMemo(() => {
    const statusParam = String(searchParams.get("status") || "").toLowerCase();
    if (
      statusParam === "open" ||
      statusParam === "pending" ||
      statusParam === "solved"
    ) {
      return statusParam;
    }
    const tabParam = String(searchParams.get("tab") || "").toLowerCase();
    if (
      tabParam === "open" ||
      tabParam === "pending" ||
      tabParam === "solved"
    ) {
      return tabParam;
    }
    return "open";
  }, [searchParams]);

  const statusFilterCount = useMemo(() => {
    // Scoped inboxes: list header shows this list's total (e.g. my solved), not the
    // global sidebar Solved bucket. Snapshot while loading avoids flashing a stale total.
    const isScopedView =
      viewParam === "assigned_to_me" ||
      viewParam === "assigned" ||
      viewParam === "unassigned" ||
      viewParam === "team";
    if (
      isScopedView &&
      !allTicketsLoading &&
      typeof ticketsPagination?.total === "number" &&
      ticketsPagination.total >= 0
    ) {
      return ticketsPagination.total;
    }
    // All inbox: only use statusCounts — never pagination.total (full list size can
    // briefly appear as the wrong Open/Pending number while a tab refetch is in flight).
    if (listStatusFilter === "pending") return ticketCounts.pending;
    if (listStatusFilter === "solved") return ticketCounts.closed;
    return ticketCounts.open;
  }, [
    viewParam,
    allTicketsLoading,
    ticketsPagination?.total,
    listStatusFilter,
    ticketCounts.pending,
    ticketCounts.open,
    ticketCounts.closed,
  ]);

  const hideListStatusFilter = useMemo(() => {
    const tab = searchParams.get("tab") || "";
    return tab === "solved" || tab === "spam" || viewParam === "spam";
  }, [searchParams, viewParam]);

  const statusFilterStaticLabel = useMemo(() => {
    const tab = searchParams.get("tab") || "";
    if (viewParam === "spam" || tab === "spam") return "Spam";
    if (tab === "solved") return "Solved";
    return "";
  }, [searchParams, viewParam]);

  const listHeaderStatusCount = useMemo(() => {
    if (hideListStatusFilter) {
      if (statusFilterStaticLabel === "Spam") return ticketCounts.spam;
      if (statusFilterStaticLabel === "Solved") return ticketCounts.closed;
    }
    return statusFilterCount;
  }, [
    hideListStatusFilter,
    statusFilterStaticLabel,
    ticketCounts.spam,
    ticketCounts.closed,
    statusFilterCount,
  ]);

  // First visit often has no status in the URL; default to open so the header
  // label matches the open-filtered list (buildTv2TicketListParams also defaults).
  useEffect(() => {
    if (!shouldDefaultListStatus(searchParams) && !searchParams.get("group_id")) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    let changed = false;
    if (shouldDefaultListStatus(searchParams)) {
      params.set("status", "open");
      if (!params.get("page")) params.set("page", "0");
      changed = true;
    }
    if (params.get("group_id")) {
      params.delete("group_id");
      changed = true;
    }
    if (changed) setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Solved status filter is only available on Assigned to Me; reset if present elsewhere.
  useEffect(() => {
    const status = String(searchParams.get("status") || "").toLowerCase();
    if (status !== "solved") return;
    const tab = searchParams.get("tab") || "";
    const isAssignedView =
      viewParam === "assigned_to_me" || viewParam === "assigned";
    if (isAssignedView || tab === "solved") return;
    const params = new URLSearchParams(searchParams);
    params.set("status", "open");
    params.set("page", "0");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, viewParam]);

  // Debounced search: reset pagination so a new query starts from page 0.
  const prevDebouncedSearchRef = useRef(debouncedTicketSearch);
  useEffect(() => {
    if (prevDebouncedSearchRef.current === debouncedTicketSearch) return;
    prevDebouncedSearchRef.current = debouncedTicketSearch;
    const params = new URLSearchParams(searchParamsRef.current);
    if ((params.get("page") || "0") !== "0") {
      params.set("page", "0");
      setSearchParams(params, { replace: true });
    }
  }, [debouncedTicketSearch, setSearchParams]);

  useEffect(() => {
    dispatch(
      getAllTickets(
        buildTv2TicketListParams(
          searchParamsRef.current,
          10,
          debouncedTicketSearchRef.current
        )
      )
    );
  }, [dispatch, listFetchKey]);

  useEffect(() => {
    const onFocus = () => {
      dispatch(
        getAllTickets({
          ...buildTv2TicketListParams(
            searchParamsRef.current,
            10,
            debouncedTicketSearchRef.current
          ),
          silent: true,
        })
      );
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch, listFetchKey]);

  useEffect(() => {
    if (!socket) return;

    const refetchList = () => {
      dispatch(
        getAllTickets({
          ...buildTv2TicketListParams(
            searchParamsRef.current,
            10,
            debouncedTicketSearchRef.current
          ),
          silent: true,
        })
      );
    };

    const tabIndexFromUrl = () =>
      tabIndexFromSearchParams(searchParamsRef.current);

    const handleTicketCreated = (data) => {
      if (!data?.ticket) return;
      const newTicket = data.ticket;
      const ticketStatus = newTicket.status?.toLowerCase();
      const tabIdx = tabIndexFromUrl();
      dispatch(adjustStatusCountsForNewTicket({ status: ticketStatus }));
      if (tabIdx === 0) {
        dispatch(addTicketOptimistic(newTicket));
      } else {
        const currentStatusTab = statusMap[tabIdx];
        if (currentStatusTab && ticketStatus === currentStatusTab) {
          dispatch(addTicketOptimistic(newTicket));
        }
      }
      refetchList();
    };

    const handleTicketStatusChanged = (data) => {
      const oldStatus = data?.oldStatus?.toLowerCase?.() ?? "";
      const newStatus = data?.newStatus?.toLowerCase?.() ?? "";
      const ticket = data?.ticket;
      const tId = ticket?._id ?? data?.ticketId;
      if (oldStatus && newStatus && oldStatus !== newStatus) {
        dispatch(adjustStatusCountsForStatusChange({ oldStatus, newStatus }));
      }
      const tabIdx = tabIndexFromUrl();
      const currentStatusTab = tabIdx !== 0 ? statusMap[tabIdx] : null;
      if (tabIdx === 0) {
        if (ticket) dispatch(updateTicketOptimistic(ticket));
      } else if (currentStatusTab) {
        if (ticket) {
          if (oldStatus === newStatus) {
            dispatch(updateTicketOptimistic(ticket));
          } else if (oldStatus === currentStatusTab && newStatus !== currentStatusTab) {
            dispatch(removeTicketOptimistic(ticket._id));
          } else if (newStatus === currentStatusTab) {
            dispatch(addTicketOptimistic(ticket));
          } else {
            dispatch(removeTicketOptimistic(ticket._id));
          }
        } else if (tId && oldStatus === currentStatusTab && newStatus !== currentStatusTab) {
          dispatch(removeTicketOptimistic(tId));
        }
      }
      refetchList();
    };

    socket.on("ticket-created", handleTicketCreated);
    socket.on("ticket-status-changed", handleTicketStatusChanged);
    return () => {
      socket.off("ticket-created", handleTicketCreated);
      socket.off("ticket-status-changed", handleTicketStatusChanged);
    };
  }, [socket, dispatch, listFetchKey]);

  const setInboxView = useCallback(
    (inboxId) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", "0");
      params.delete("team_id");
      if (inboxId === "all") {
        params.delete("tab");
        params.delete("view");
        params.set("status", "open");
      } else if (inboxId === "closed") {
        params.set("tab", "solved");
        params.delete("view");
        params.delete("status");
      } else if (inboxId === "spam") {
        params.delete("tab");
        params.set("view", "spam");
        params.delete("status");
      } else if (inboxId === "assigned_to_me" || inboxId === "assigned") {
        params.delete("tab");
        params.set("view", "assigned_to_me");
        params.set("status", "open");
      } else if (inboxId === "unassigned") {
        params.delete("tab");
        params.set("view", "unassigned");
        params.set("status", "open");
      } else if (typeof inboxId === "string" && inboxId.startsWith("team:")) {
        const teamId = inboxId.slice("team:".length);
        params.delete("tab");
        params.set("view", "team");
        if (teamId) params.set("team_id", teamId);
        else params.delete("team_id");
        params.set("status", "open");
      } else {
        params.delete("view");
        params.delete("status");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const displayedTicketsRaw = useMemo(() => {
    return Array.isArray(allTickets) ? [...allTickets] : [];
  }, [allTickets]);

  const listSort = useMemo(() => {
    const sortByRaw = (searchParams.get("sort_by") || "").trim();
    const sort_by = sortByRaw === "updatedAt" ? sortByRaw : null;
    const order =
      String(searchParams.get("order") || "desc").toLowerCase() === "asc"
        ? "asc"
        : "desc";
    return { sort_by, order };
  }, [searchParams]);

  const selectedTicketId = ticketIdParam || null;

  useEffect(() => {
    processedMessageIdsRef.current = new Set();
    if (!selectedTicketId) {
      dispatch(resetMessagesState());
      return undefined;
    }
    dispatch(resetMessagesState());
    dispatch(getTicketById(selectedTicketId));
    dispatch(getTicketMessagesById(selectedTicketId));
    return undefined;
  }, [dispatch, selectedTicketId]);

  useEffect(() => {
    if (!socket || !selectedTicketId) return;
    socket.emit("joinTicket", selectedTicketId);
    return () => {
      socket.emit("leaveTicket", selectedTicketId);
    };
  }, [socket, selectedTicketId]);

  useEffect(() => {
    if (!socket || !selectedTicketId) return;

    const handleNewMessage = (payload) => {
      if (payload.ticketId !== selectedTicketId) return;
      const messageId = `${payload.ticketId}-${payload.message}-${payload.sender}`;
      if (processedMessageIdsRef.current.has(messageId)) return;
      processedMessageIdsRef.current.add(messageId);
      dispatch({
        type: "getTicketMessagesById/optimisticAdd",
        payload: {
          type: "user",
          message: payload.message,
          sender: payload.sender,
          created_at: new Date().toISOString(),
          file_image: payload.attachments?.[0]?.url,
          _id: Date.now().toString(),
        },
      });
      dispatch(
        getTicketMessagesById({
          ticketId: selectedTicketId,
          silent: true,
        })
      );
    };

    const handleSentMessage = (payload) => {
      if (payload.ticketId !== selectedTicketId) return;
      if (payload.agentId == userData?.response?.user?._id) return;
      const msg = payload.message;
      const messageId = `${payload.ticketId}-${msg?._id || msg?.message}-${msg?.sender}`;
      if (processedMessageIdsRef.current.has(messageId)) return;
      processedMessageIdsRef.current.add(messageId);
      dispatch({
        type: "getTicketMessagesById/optimisticAdd",
        payload: {
          type: msg?.type || "agent",
          message: msg?.message,
          sender: msg?.sender,
          created_at: new Date().toISOString(),
          file_image: msg?.attachments?.[0]?.url,
          attachments: msg?.attachments,
          isAdmin: msg?.isAdmin,
          isPrivate: msg?.isPrivate,
          agent: {
            ...msg?.agent,
            agent_image: msg?.agent?.agent_image || msg?.senderImage || null,
          },
          _id: msg?._id || Date.now().toString(),
        },
      });
      dispatch(
        getTicketMessagesById({
          ticketId: selectedTicketId,
          silent: true,
        })
      );
    };

    socket.on("ticket:message:new", handleNewMessage);
    socket.on("ticket:message:sent", handleSentMessage);
    return () => {
      socket.off("ticket:message:new", handleNewMessage);
      socket.off("ticket:message:sent", handleSentMessage);
    };
  }, [socket, selectedTicketId, dispatch, userData?.response?.user?._id]);

  useEffect(() => {
    if (!socket || !selectedTicketId) return;
    const handleTicketUpdate = (data) => {
      const tid =
        data?.ticketId ??
        data?._id ??
        data?.ticket?._id ??
        data?.ticket?.id ??
        data?.id;
      if (tid && String(tid) !== String(selectedTicketId)) return;

      // Only refresh ticket details when assignee (or other ticket fields) changed —
      // not on every chat message.
      const nextAssignee = data?.assignee ?? data?.ticket?.assignee;
      const hasAssigneeUpdate = nextAssignee !== undefined;
      const hasTicketFieldUpdate =
        hasAssigneeUpdate ||
        data?.status != null ||
        data?.priority != null ||
        data?.groups != null ||
        data?.ticket?.status != null ||
        data?.ticket?.priority != null;

      if (!hasTicketFieldUpdate) {
        dispatch(
          getTicketMessagesById({
            ticketId: selectedTicketId,
            silent: true,
          })
        );
        return;
      }

      dispatch(
        getTicketMessagesById({
          ticketId: selectedTicketId,
          silent: true,
        })
      );
      dispatch(getTicketById({ ticketId: selectedTicketId, silent: true }))
        .unwrap()
        .then((ticketRes) => {
          const updatedTicket =
            ticketRes?.data?.ticket ?? ticketRes?.data ?? ticketRes?.ticket ?? null;
          if (updatedTicket?._id) {
            dispatch(updateTicketOptimistic(updatedTicket));
          }
        })
        .catch(() => {});
    };
    socket.on("ticket:update", handleTicketUpdate);
    return () => {
      socket.off("ticket:update", handleTicketUpdate);
    };
  }, [socket, selectedTicketId, dispatch]);

  const listRow = useMemo(
    () => displayedTicketsRaw.find((t) => t._id === selectedTicketId) || null,
    [displayedTicketsRaw, selectedTicketId]
  );

  const detailTicket = ticketDetailData?.ticket || null;
  const selectedTicket = useMemo(
    () => buildTv2Ticket(listRow, detailTicket, selectedTicketId),
    [listRow, detailTicket, selectedTicketId]
  );
  const selectedHeaderTitle = useMemo(() => {
    if (!selectedTicketId) return "";
    const rawName =
      detailTicket?.createdBy?.name ||
      detailTicket?.createdBy?.full_name ||
      detailTicket?.created_by?.name ||
      detailTicket?.customerName ||
      detailTicket?.requester?.name ||
      listRow?.createdBy?.name ||
      listRow?.customerName ||
      listRow?.requester?.name ||
      selectedTicket?.createdBy?.name ||
      selectedTicket?.customerName ||
      selectedTicket?.requester?.name ||
      "";
    const normalized = String(rawName || "").trim();
    if (!normalized || normalized.toLowerCase() === "unknown") return "";
    return normalized;
  }, [selectedTicketId, detailTicket, listRow, selectedTicket]);

  const handleSelectTicket = useCallback(
    (mongoId) => {
      setCreateTicketOpen(false);
      const params = new URLSearchParams(searchParams);
      if (mongoId) params.set("ticketId", mongoId);
      else params.delete("ticketId");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleCreateTicketSuccess = useCallback(
    (created) => {
      setCreateTicketOpen(false);
      dispatch(
        getAllTickets({
          ...buildTv2TicketListParams(
            searchParams,
            10,
            debouncedTicketSearchRef.current
          ),
          silent: true,
        })
      );
      const newId = created?._id;
      if (newId) {
        const p = new URLSearchParams(searchParams);
        p.set("ticketId", newId);
        setSearchParams(p);
      }
    },
    [dispatch, searchParams, setSearchParams]
  );

  const goOpenTicketsTab = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", "open");
    params.set("status", "open");
    params.delete("view");
    params.delete("team_id");
    params.set("page", "0");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleListStatusFilterChange = useCallback(
    (nextStatus) => {
      if (
        nextStatus !== "open" &&
        nextStatus !== "pending" &&
        nextStatus !== "solved"
      ) {
        return;
      }
      const params = new URLSearchParams(searchParams);
      params.set("status", nextStatus);
      if (!params.get("view")) {
        // Keep All inbox selected; solved status filter must not activate Solve tab.
        if (nextStatus === "solved") {
          params.delete("tab");
        } else {
          params.set("tab", nextStatus);
        }
      } else {
        params.delete("tab");
      }
      params.set("page", "0");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleListSortChange = useCallback(
    ({ sort_by: nextSortBy, order: nextOrder }) => {
      if (nextOrder !== "asc" && nextOrder !== "desc") return;
      const params = new URLSearchParams(searchParams);
      if (nextSortBy === "updatedAt") {
        params.set("sort_by", nextSortBy);
      } else {
        params.delete("sort_by");
      }
      params.set("order", nextOrder);
      params.delete("sortField");
      params.delete("sortOrder");
      params.set("page", "0");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const inboxActiveId = useMemo(() => {
    if (viewParam === "assigned_to_me" || viewParam === "assigned") {
      return "assigned_to_me";
    }
    if (viewParam === "unassigned") return "unassigned";
    if (viewParam === "spam") return "spam";
    if (viewParam === "team") return `team:${searchParams.get("team_id") || ""}`;
    const tab = searchParams.get("tab");
    if (tab === "solved") return "closed";
    if (tab === "spam") return "spam";
    return "all";
  }, [searchParams, viewParam]);

  const invalidateTicketList = useCallback(() => {
    dispatch(
      getAllTickets({
        ...buildTv2TicketListParams(
          searchParams,
          10,
          debouncedTicketSearchRef.current
        ),
        silent: true,
      })
    );
  }, [dispatch, searchParams]);

  const loadMoreTickets = useCallback(() => {
    if (!ticketsInitialLoaded || listLoadingMore || listExhausted) {
      return;
    }
    const baseParams = buildTv2TicketListParams(
      searchParams,
      10,
      debouncedTicketSearchRef.current
    );
    const limit = baseParams.limit || 10;
    if (allTickets.length === 0) return;

    // API pagination.page is 1-based; client getAllTickets sends page+1.
    const currentApiPage =
      typeof ticketsPagination?.page === "number" && ticketsPagination.page > 0
        ? ticketsPagination.page
        : Math.ceil(allTickets.length / limit);
    const nextPage = currentApiPage; // 0-based index for the next page

    dispatch(
      getAllTickets({
        ...baseParams,
        page: nextPage,
        append: true,
        silent: true,
      })
    );
  }, [
    dispatch,
    searchParams,
    ticketsInitialLoaded,
    listLoadingMore,
    listExhausted,
    allTickets.length,
    ticketsPagination?.page,
  ]);

  const listHasMore = ticketsInitialLoaded && !listExhausted;

  const handleToggleTicketsNav = useCallback(() => {
    setTicketsNavOpen((prev) => {
      const next = !prev;
      if (next) {
        dispatch(
          getAllTickets({
            ...buildTv2TicketListParams(
              searchParams,
              10,
              debouncedTicketSearchRef.current
            ),
            silent: true,
          })
        );
      }
      return next;
    });
  }, [dispatch, searchParams]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 0px)",
        bgcolor: "#fff",
        fontFamily: "Inter, sans-serif",
        color: "#061522",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: ticketsNavOpen ? TICKETS_NAV_SIDEBAR_WIDTH : 0,
          flexShrink: 0,
          overflow: "hidden",
          transition: "width 0.2s ease",
          borderRight: ticketsNavOpen ? "1px solid #E5E9EC" : "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: TICKETS_NAV_SIDEBAR_WIDTH,
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TicketsSidebar
            ticketCounts={ticketCounts}
            activeInboxId={inboxActiveId}
            onInboxSelect={setInboxView}
            onCreateTicket={() => setCreateTicketOpen(true)}
            searchQuery={ticketSearch}
            onSearchChange={setTicketSearch}
          />
        </Box>
      </Box>
      <TicketList
        tickets={displayedTicketsRaw}
        loading={allTicketsLoading && displayedTicketsRaw.length === 0}
        selectedId={selectedTicketId}
        onSelect={handleSelectTicket}
        onToggleTicketsNav={handleToggleTicketsNav}
        headerTitle={selectedHeaderTitle}
        statusFilter={listStatusFilter}
        statusFilterCount={listHeaderStatusCount}
        statusFilterCountLoading={
          allTicketsLoading || statusCountRefreshRequests > 0
        }
        onStatusFilterChange={handleListStatusFilterChange}
        hideStatusFilter={hideListStatusFilter}
        statusFilterOptions={listStatusFilterOptions}
        statusFilterStaticLabel={statusFilterStaticLabel}
        sortBy={listSort.sort_by}
        sortOrder={listSort.order}
        onSortChange={handleListSortChange}
        listHasMore={listHasMore}
        onNearListEnd={loadMoreTickets}
        searchQuery={debouncedTicketSearch}
        onBulkApplied={invalidateTicketList}
      />
      {createTicketOpen ? (
        <>
          <Box
            component="section"
            sx={{
              flex: "1 1 592px",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                height: TV2_COLUMN_HEADER_HEIGHT,
                minHeight: TV2_COLUMN_HEADER_HEIGHT,
                flexShrink: 0,
                px: 3,
                borderBottom: "1px solid #E5E9EC",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#061522",
                    fontFamily: "Inter, sans-serif",
                    lineHeight: "18px",
                  }}
                >
                  Create ticket
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: "#405261",
                    fontFamily: "Inter, sans-serif",
                    mt: 0.25,
                  }}
                >
                  New ticket
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: { xs: 2, sm: 3 },
                py: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 592 }}>
                <CreateTicket
                  embedded
                  onCancel={() => setCreateTicketOpen(false)}
                  onCreated={handleCreateTicketSuccess}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: detailsOpen ? 280 : 0,
              flexShrink: 0,
              minHeight: 0,
              overflow: "hidden",
              transition: "width 0.2s ease",
              borderLeft: detailsOpen ? "1px solid #E5E9EC" : "none",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                width: 280,
                height: "100%",
                minHeight: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TicketDetailsEmpty onToggleDetails={() => setDetailsOpen((v) => !v)} />
            </Box>
          </Box>
        </>
      ) : selectedTicketId && selectedTicket?._id ? (
        <>
          <TicketChat
            ticket={selectedTicket}
            ticketMessages={ticketMessages}
            ticketId={selectedTicketId}
            ticketRaw={detailTicket || listRow}
            messagesLoading={messagesLoading}
            ticketDetailLoading={ticketDetailLoading}
            detailsOpen={detailsOpen}
            onToggleDetails={() => setDetailsOpen((v) => !v)}
            onSubjectSaved={invalidateTicketList}
          />
          <Box
            sx={{
              width: detailsOpen ? 280 : 0,
              flexShrink: 0,
              minHeight: 0,
              overflow: "hidden",
              transition: "width 0.2s ease",
              borderLeft: detailsOpen ? "1px solid #E5E9EC" : "none",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                width: 280,
                height: "100%",
                minHeight: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TicketDetails
                ticket={selectedTicket}
                ticketDetailLoading={ticketDetailLoading}
                onToggleDetails={() => setDetailsOpen((v) => !v)}
                onTicketListInvalidate={invalidateTicketList}
              />
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#8B97A2",
            fontSize: 14,
          }}
        >
          Select a ticket to view conversation
        </Box>
      )}
    </Box>
  );
}

export default TicketV2;
